import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { action, computed, makeObservable, observable } from "mobx";
import { ListStore } from "Stores/RESTStore/ListStore";
import { groupGUIStore, groupListStore } from "./GroupsStore";
import { groupGroupsMembersListStore } from "./GroupGroupsMembers";
import { stringSort } from "utils/defaultSort";
import { TableSorter } from "../Pages/Components/MemberList";

export interface TransmittedMember {
  id: number;
  memberType: API_GROUPMEMBER_MEMBERTYPE;
}

class MemberListStore extends ListStore<API_GroupMember> {
  url(): string {
    // Fixme: Temp URL
    return `api/projects/${projectsDataStore.projectId}/groups/${groupGUIStore.currentGroupId}/unassignedMembers`;
  }

  getMember(member: TransmittedMember) {
    return this.list.find((val) => val.id === member.id && val.memberType === member.memberType);
  }
}

export const memberListStore = new MemberListStore();

class GroupMemberStore extends ListStore<API_GroupMember> {
  groupId: number;
  sorter: TableSorter;
  searchValue: string = "";

  constructor() {
    super();
    this.sorter = { columnKey: "name", order: "ascend" };

    makeObservable(this, {
      searchValue: observable,
      addMembers: action,
      removeMember: action,
      addGroupMembers: action,
      removeGroupMember: action,
      sort: action,
      handleSearch: action,
      assignableMembers: computed,
      members: computed,
    });
  }

  url(): string {
    return API_URL.GROUPS.getGroupMembers(projectsDataStore.projectId, groupGUIStore.currentGroupId);
  }

  handleSearch(searchValue: string) {
    this.searchValue = searchValue;
  }

  addGroupMembers(memberList: TransmittedMember[]) {
    groupGUIStore.addSubGroupsToCurrent(memberList);
    for (const m of memberList) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const g = groupListStore.getItem(m as any);
      for (const m2 of g.groupMembers) {
        const idx = this.list.findIndex((m3) => m3.id === m2.id && m3.memberType === m2.memberType);
        if (idx > -1) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          this.list[idx].memberFromSubGroups.push(m as any);
          this.list[idx].memberFromSubGroups = this.list[idx].memberFromSubGroups.slice();
        } else {
          m2.explicitMember = false;
          m2.memberFromSubGroups = [g.id];
          this.list.push(m2);
        }
      }
    }
    this.sort();
  }

  removeGroupMember(subGroupId) {
    groupGUIStore.removeSubGroupsFromCurrent(subGroupId);
    const toRemove = [];
    for (const member of this.list) {
      const idx = member.memberFromSubGroups.findIndex((id) => subGroupId === id);
      if (idx > -1) {
        member.memberFromSubGroups.splice(idx, 1);
      }
      if (!member.explicitMember && member.memberFromSubGroups.length === 0) {
        toRemove.push(member);
      }
    }
    for (const m of toRemove) {
      this.list.remove(m);
    }
  }
  sort(prop?: keyof API_GroupMember, dir?: "ASC" | "DESC") {
    const newProp = prop || this.sorter.columnKey;
    const newDir = dir || this.sorter.order === "ascend" ? "ASC" : "DESC";
    this.list.sort((a, b) => stringSort(a, b, newProp as keyof API_GroupMember, newDir));
  }

  addMembers(memberList: TransmittedMember[]) {
    let haveToResort = false;
    memberList.forEach((val) => {
      const m = memberListStore.getMember(val);
      const idx = this.list.findIndex((m) => m.id === val.id && m.memberType === val.memberType);
      if (idx > -1) {
        this.list[idx] = { ...this.list[idx], explicitMember: true };
      } else {
        this.list.push(m);
        haveToResort = true;
      }
    });
    haveToResort && this.sort();
  }

  removeMember(member: API_GroupMember) {
    this.list.remove(member);
    if (member.memberFromSubGroups.length > 0) {
      member.explicitMember = false;
      this.list.push(member);
      this.sort();
    }
  }

  searchFilter(member: API_GroupMember): boolean {
    if (this.searchValue === "") return true;
    const searchVal = this.searchValue?.toLowerCase();
    return (
      member.company?.toLowerCase().indexOf(searchVal) > -1 ||
      member.firstName?.toLowerCase().indexOf(searchVal) > -1 ||
      member.surname?.toLowerCase().indexOf(searchVal) > -1
    );
  }

  get assignableGroupMembers() {
    return groupGroupsMembersListStore.list;
  }

  get assignableMembers() {
    return memberListStore.list;
  }

  get members() {
    return this.list.filter((val) => this.searchFilter(val));
  }
}

export const groupMemberStore = new GroupMemberStore();
